import { z } from 'zod';
import { SUGGESTED_STATUS } from '../schemas/terra-signals';
import { FlowExposureKindSchema } from '../schemas/flow-play';
import { GroupLogicSchema } from './filter';
import type { FilterCondition, FilterGroup, GroupLogic } from './filter';
import type { FilterFieldCatalogEntry } from './filter-fields';
import { withHints, PLAYS_FIELD_HINTS } from './field-hints';

/**
 * `GET /api/flow/plays` wire contract (plan
 * `plans/2026-09-09-003-terra-signals-plays-tab-plan.md` U1, D9).
 *
 * One row per play (event campaign, Gong flow, or singleton play type) with
 * Suggested / Invited-Sent / Responded counts folded in from
 * `countTerraSignals` (`src/services/terra-signals/query.ts`). Types are
 * `z.infer` only (`terra/no-parallel-schema-and-type`) — never hand-declare
 * a parallel interface here.
 */

/**
 * Display-order groups for the `scope=all` accordion (D11): Events, Flows,
 * Intros, Messages, then any other active play type by name. Product
 * taxonomy from the Notion spec, not derived from any DB column.
 */
export const PLAY_TYPE_GROUPS = ['Events', 'Flows', 'Intros', 'Messages'] as const;
export type PlayTypeGroup = (typeof PLAY_TYPE_GROUPS)[number];

/**
 * `play_type` slug → accordion group. A play type whose slug is not a key
 * here groups under its own `play_type_name` instead (D11) — this map is
 * intentionally not exhaustive over every possible `flow_play_types` row.
 *
 * Every value here must be one of `PLAY_TYPE_GROUPS` — pinned by a contract
 * test (`src/api/__tests__/flow-plays-contract.test.ts`).
 */
export const PLAY_SLUG_GROUP: Record<string, PlayTypeGroup> = {
  event_invite: 'Events',
  gong_flow: 'Flows',
  warm_intro: 'Intros',
  generate_message: 'Messages',
  generate_message_workflow: 'Messages',
};

/**
 * Play scope builders (plan `2026-09-10-001-plays-tab-filter-redesign-plan.md`
 * D11/D12, amendments **A16/A5**) — the single home for every builder the plan
 * spreads across U5/U6/U7. A "play scope" is the one `FilterGroup` (stable id
 * `PLAY_SCOPE_GROUP_ID`) a Plays-row Actions-menu item applies to the Contact
 * component's working filters: it scopes by the play's NAME via
 * `signal_name contains "<play name>"` plus the predicates for one of the
 * three preset kinds, or (for Warm Intro rows) `warm_intro_available = true`.
 *
 * A16 moves the scope from `signal_subtype equals` (A4) to
 * **`signal_name contains`**. `signal_name` is the view's own human-readable
 * formula — `"<subtype> · <status>"`-shaped — so ONE field expresses both
 * "which play" and, for the Suggested preset, "which status", and the rep
 * reads the same words the Signal Name column shows them. Two consequences,
 * both accepted explicitly by QA round 3:
 *
 *  - `contains` is a SUBSTRING match, so a scope on `Sculpt 2026` also
 *    matches `Sculpt 2026 Dinner`. Accepted: a rep scoping to a play by name
 *    reading it as a prefix is the expected behaviour, and the alternative
 *    (`equals` on a formula value) is not something a rep could hand-rebuild.
 *  - Recurring events that share a name merge, in the preset and in the
 *    counts. Unchanged from A4 (~6 such names locally).
 *
 * A16 also keeps A1's reversal standing: `signal_instance_id` is gone from
 * the contact filter catalog and from every preset. The rule is "nothing
 * under the hood" — every condition a rep sees is the condition that runs,
 * with a human-readable value, so a rep can hand-rebuild any preset in the
 * Filters popover and get identical rows.
 *
 * DOCUMENTED DIVERGENCE (A16): the plays-row COUNTS
 * (`countPlaysByKind`, `src/services/flow/plays.ts`) still scope by
 * `signal_subtype in_any_of <page play names>` — an EXACT match — because
 * they must group by a single play identity and `groupBy` needs an exact key.
 * So a count and its chip can differ for exactly one shape: when one play's
 * name is a substring of another's, the chip is wider than the count.
 * Accepted; the status half of each preset is still shared verbatim via
 * `playScopeConditions`, so no other drift is possible.
 *
 * A5: a preset NEVER writes `FilterGroup.label`. The group renders as its
 * plain conditions, so what the rep reads is what compiles. (`label` may
 * still exist on the schema for saved views; presets don't set it, and
 * `matchPlayScope` has never read it.)
 *
 * `playScopeConditions(kind)` is also what `listFlowPlays` (U7/U11) uses to
 * derive counts, so a count and its chip can only disagree if the compiler
 * itself is wrong — never because two code paths encode the same preset
 * differently.
 */

/** The one `FilterGroup.id` a play-scope preset group ever carries. */
export const PLAY_SCOPE_GROUP_ID = 'chip-play-scope';

/** The three Plays presets a Campaign (event) row can apply. */
export const PLAY_SCOPE_KINDS = ['suggested', 'invited', 'responded'] as const;
export type PlayScopeKind = (typeof PLAY_SCOPE_KINDS)[number];

/**
 * Rep-facing label for each `PlayScopeKind` — the Plays-table count headers.
 * NOT chip text: under A5 a preset writes no `FilterGroup.label`, so the
 * chip renders its plain conditions.
 *
 * `invited` reads "Not Responded" (2026-09-22 plan) — the bucket's LOGIC is
 * unchanged (`playScopeConditions('invited')` below is untouched: it is
 * still the complement of Suggested, which includes `HiL/Awaiting`,
 * `Invited`, `Sent`, `Registered`, and any other non-Suggested status with
 * `has_responded = false`), only the label. "Invited" read as "every row
 * I've invited", which is wrong for a HiL/Awaiting or Declined row; "Not
 * Responded" names what the bucket actually is. `invited_sent` (the key on
 * `PLAY_SCOPE_KINDS`/`playScopeConditions`, and the `PLAYS_COLUMNS` count
 * key below) is UNCHANGED — this is a label-only change, see
 * `NOT_RESPONDED_TOOLTIP`.
 */
export const PLAY_SCOPE_LABELS: Record<PlayScopeKind, string> = {
  suggested: 'Suggested',
  invited: 'Not Responded',
  responded: 'Responded',
};

/**
 * The tooltip explaining what "Not Responded" actually covers — the
 * Plays-table column header (`playsTableConfig.tsx`'s `headerTitle`, the
 * existing `FlowColumnDef.headerTitle` pattern) is the only place a rep can
 * learn this without opening the Filters popover and reading the compiled
 * conditions.
 */
export const NOT_RESPONDED_TOOLTIP =
  'Everyone invited who has not yet responded — includes Invited, Sent, Registered, Declined, and HiL/Awaiting (a Terra send Salesforce has not mirrored back yet).';

/**
 * The status predicates for one preset kind, WITHOUT the leading
 * `signal_name contains "<play name>"` scope condition — this is what
 * `buildPlayScopeGroup` appends after the name condition, and what
 * `listFlowPlays` (U7/U11) reuses verbatim (with
 * `signal_subtype in_any_of <page play names>` in the scope condition's
 * place) so counts and chips share one definition of each preset's STATUS
 * half.
 *
 * A16: `suggested` is now a SECOND `signal_name contains` row —
 * `contains "Suggested"` — rather than `signal_status equals "Suggested"`.
 * `signal_name` carries the status token verbatim (the view's formula
 * appends it), so the whole Suggested preset reads as two conditions on one
 * field, which is what a rep sees in the Signal Name column. `invited` and
 * `responded` KEEP their `signal_status` / `has_responded` rows, because
 * neither "Invited" nor "Responded" is a token `signal_name` is guaranteed to
 * carry: `invited` is the complement of Suggested (every non-Suggested
 * status — `HiL/Awaiting`, `Invited`, `Sent`, `Registered`, …), and
 * `responded` is a boolean flag, not a status word.
 */
export function playScopeConditions(kind: PlayScopeKind): FilterCondition[] {
  switch (kind) {
    case 'suggested':
      return [
        { id: 'chip-play-scope-1', field: 'signal_name', operator: 'contains', value: SUGGESTED_STATUS },
      ];
    case 'invited':
      return [
        { id: 'chip-play-scope-1', field: 'signal_status', operator: 'not_equals', value: SUGGESTED_STATUS },
        { id: 'chip-play-scope-2', field: 'has_responded', operator: 'equals', value: 'false' },
      ];
    case 'responded':
      return [
        { id: 'chip-play-scope-1', field: 'has_responded', operator: 'equals', value: 'true' },
      ];
  }
}

/**
 * Builds the play-scope `FilterGroup` for a Campaign (event) row's
 * Suggested/Invited/Responded menu item. Scoped by the play's NAME via
 * `signal_name contains name` (amendment **A16**, replacing A4's
 * `signal_subtype equals`) and carrying NO `label` (amendment A5) — the rep
 * reads the conditions that actually run, in the same words the Signal Name
 * column shows. Plays whose names share a substring merge under one preset;
 * that is the accepted consequence of a name-based, id-free scope (see the
 * section doc above).
 */
export function buildPlayScopeGroup({ kind, name }: { kind: PlayScopeKind; name: string }): FilterGroup {
  return {
    id: PLAY_SCOPE_GROUP_ID,
    logic: 'AND',
    conditions: [
      { id: 'chip-play-scope-0', field: 'signal_name', operator: 'contains', value: name },
      ...playScopeConditions(kind),
    ],
  };
}

/** Builds the play-scope `FilterGroup` for a Warm Intro row's "Filter for Available" menu item. No `label` (A5). */
export function buildWarmIntroScopeGroup(): FilterGroup {
  return {
    id: PLAY_SCOPE_GROUP_ID,
    logic: 'AND',
    conditions: [
      { id: 'chip-play-scope-0', field: 'warm_intro_available', operator: 'equals', value: 'true' },
    ],
  };
}

/**
 * The group id `SignalScopePrompt`'s "Pick which play first" Apply (U5)
 * writes/replaces — a SEPARATE id from `PLAY_SCOPE_GROUP_ID` because this
 * group is the frontend's own narrowing filter (added when a rep picks a
 * play for a Signal sort/group on the default, un-narrowed People table),
 * not a play-scope preset decoded by `matchPlayScope`. Distinct so applying
 * one never clobbers the other.
 */
export const SIGNAL_NARROWING_GROUP_ID = 'chip-signal-narrow';

/**
 * Builds the one-condition `FilterGroup` that narrows a Signal sort/group
 * request to a single play by name (U4/U5, Item A) — `signal_subtype
 * equals <name>` is an EXACT match on the play name column (not `signal_name
 * contains`, which `buildPlayScopeGroup` uses for its broader preset scope),
 * so this group alone satisfies `requestNarrowsSignals`
 * (`signal-narrowing.ts`): `signal_subtype` is a `SIGNAL_NARROWING_FIELDS`
 * member and `equals` is a `NARROWING_OPERATORS` member.
 */
export function buildSignalNarrowingGroup(name: string): FilterGroup {
  return {
    id: SIGNAL_NARROWING_GROUP_ID,
    logic: 'AND',
    conditions: [
      { id: 'chip-signal-narrow-1', field: 'signal_subtype', operator: 'equals', value: name },
    ],
  };
}

/**
 * The active play scope, decoded from a set of working `FilterGroup`s — see
 * `matchPlayScope`. `name` (A4), never an id: it is both the scoping value
 * and what the Plays table highlights on (EVERY row whose `name` matches,
 * since a name can be shared).
 */
export type PlayScope = { kind: PlayScopeKind; name: string } | { kind: 'warm_intro' };

/** Structural (field/operator/value) equality for two condition lists — ignores `id`, so a scope group survives id churn. */
function conditionsMatch(a: FilterCondition[], b: FilterCondition[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((cond, i) => {
    const other = b[i];
    return cond.field === other.field && cond.operator === other.operator && JSON.stringify(cond.value) === JSON.stringify(other.value);
  });
}

/**
 * The SHAPE half of the decode: structurally matches the
 * `PLAY_SCOPE_GROUP_ID` group's conditions against the known presets,
 * IGNORING both combinators. Split out from `matchPlayScope` so the two
 * questions stay separable — "is this the shape a preset builds?" and "is
 * the expression it decodes to still a scope?" (A25's gates).
 *
 * NEVER reads `label` (presets no longer write one at all under A5, and a
 * stray label on a restored saved view must not break the read-back, D8).
 *
 * Exported so a caller can check one group's shape in isolation (call it with
 * a single-element array, `[group]`) without the outer-`groupLogic` gate
 * `matchPlayScope` layers on top for the FULL working-filter read-back.
 */
export function matchPlayScopeShape(groups: FilterGroup[]): PlayScope | null {
  const group = groups.find((g) => g.id === PLAY_SCOPE_GROUP_ID);
  if (!group) return null;

  if (conditionsMatch(group.conditions, buildWarmIntroScopeGroup().conditions)) {
    return { kind: 'warm_intro' };
  }

  const [first, ...rest] = group.conditions;
  // A16 — the scope condition is `signal_name contains "<play name>"`. The
  // retired shapes (`signal_instance_id equals <id>`, A1; `signal_subtype
  // equals <name>`, A4) both fail this check and decode to "no active
  // scope" — no highlight, no menu check, no A10. They are NOT deleted for
  // it (A27): only an A1 group goes, and it goes for naming a retired FIELD.
  if (!first || first.field !== 'signal_name' || first.operator !== 'contains' || typeof first.value !== 'string') {
    return null;
  }
  const name = first.value;

  for (const kind of PLAY_SCOPE_KINDS) {
    if (conditionsMatch(rest, playScopeConditions(kind))) {
      return { kind, name };
    }
  }
  return null;
}

/**
 * Decodes the ACTIVE `PlayScope` — the play the Plays list highlights, the
 * menu item that shows a check, and the play whose count drives A10's
 * mirror-gap empty state — from the Contact component's working
 * `FilterGroup`s plus its OUTER combinator.
 *
 * Shape alone is not enough (A25 / Codex round-3 **#4**). A preset is only
 * the active scope while the expression it decodes to is still the narrowing
 * the preset built, and a rep can break that without editing one condition:
 *
 *  - **the preset group's own `logic`.** `Responded` flipped to OR runs
 *    `signal_name contains "<name>" OR has_responded = true` — everyone on
 *    that play PLUS everyone who responded to any other play. Structurally
 *    identical, semantically the opposite of a scope.
 *  - **the outer `groupLogic`, when more than one group exists.** The preset
 *    is then one BRANCH of a union rather than a narrowing, so the rows on
 *    screen are not "this play, this status" and nothing may claim they are.
 *    A single group is exempt: the union of one branch IS that branch, so an
 *    outer OR over the preset alone still scopes.
 *
 * `groupLogic` defaults to `'AND'` so every call site that has no outer
 * combinator to report (and every pre-A25 one) behaves exactly as before.
 *
 * Returns `null` when no scope group exists, when one exists whose shape
 * matches no known preset (the retired A1 `signal_instance_id` and A4
 * `signal_subtype` shapes included — a stale tab decodes to "no active
 * scope" rather than a phantom one), or when either gate above trips.
 */
export function matchPlayScope(groups: FilterGroup[], groupLogic: GroupLogic = 'AND'): PlayScope | null {
  const group = groups.find((g) => g.id === PLAY_SCOPE_GROUP_ID);
  if (!group || group.logic !== 'AND') return null;
  if (groupLogic === 'OR' && groups.length > 1) return null;
  return matchPlayScopeShape(groups);
}

/**
 * `suggested`/`responded` are `null` for non-event plays (Gong flows,
 * singletons) — rendered as a dash on the client, never coerced to 0 (D9,
 * plan U1 Approach step 5). `invited_sent` is always a number: every play
 * kind has at least a "queued/sent" concept.
 *
 * `invite_subject`/`invite_body` (amendment A8) are the event invite's own
 * copy, straight off `launcher_sfdc_campaigns` — hidden columns by default,
 * rendered truncated with the full text on hover. Nullable on every play
 * kind, and always `null` on Gong-flow and singleton rows: those play kinds
 * have no invite copy of their own at all, so `null` here is the honest
 * schema value, not a placeholder for something unfetched.
 *
 * `capacity` is the campaign's Salesforce `Capacity__c` (already mirrored
 * into `launcher_sfdc_campaigns.capacity`) — a raw integer, `null` when the
 * campaign has none set (rendered as a dash, never coerced to 0).
 */
export const FlowPlaysRowSchema = z.object({
  play_id: z.string(),
  play_type: z.string(),
  play_type_name: z.string(),
  exposure_kind: FlowExposureKindSchema,
  name: z.string(),
  meta: z.string().nullable(),
  campaign_type: z.string().nullable(),
  status: z.string().nullable(),
  start_date: z.string().nullable(),
  city: z.string().nullable(),
  target_persona: z.string().nullable(),
  playhouse_visible: z.boolean().nullable(),
  invite_subject: z.string().nullable(),
  invite_body: z.string().nullable(),
  capacity: z.number().int().nullable(),
  counts: z.object({
    suggested: z.number().int().nullable(),
    invited_sent: z.number().int(),
    responded: z.number().int().nullable(),
  }),
});
export type FlowPlaysRow = z.infer<typeof FlowPlaysRowSchema>;

/**
 * A26 — the Plays list is EVENTS ONLY, so the response is just the rows.
 *
 * `gong_flow_total` and `gong_rows_materialized` are DELETED along with the
 * withheld-Gong machinery they described. That mechanism existed to answer
 * "how many Gong flows would match?" WITHOUT running the filter, and it was
 * wrong twice in two review rounds for the same structural reason: the
 * decision inspected one `FilterGroup` in isolation while the SQL ran the
 * whole expression. Under an outer OR (`Playhouse Visible` OR
 * `Play Type = Gong Flow`) it suppressed every Gong row the SQL had
 * correctly selected; inverted, it advertised "Load Gong flows (660)" and
 * every one of those rows was filtered away on arrival (Codex round-3 #1).
 *
 * Gong flows are ordinary plays. When they return they return through this
 * query like every other row, paged by `FlowTable`'s normal load-more.
 */
export const FlowPlaysResponseSchema = z.object({
  plays: z.array(FlowPlaysRowSchema),
});
export type FlowPlaysResponse = z.infer<typeof FlowPlaysResponseSchema>;

/**
 * Plain (non-strict) `z.object`, same rationale as `ContactsQuerySchema`
 * (`src/api/flow.ts:552`) — a stale tab still sending `scope=`/`q=`/`type=`
 * (plan `2026-09-10-001-plays-tab-filter-redesign-plan.md` amendment A2,
 * unit U9) gets those keys silently stripped by Zod rather than a 400; the
 * request just falls back to `filters`'s default (`[]`, "every play").
 *
 * UNLIKE `ContactsQuerySchema.filters` (`src/api/flow.ts:644-650`, which
 * swallows invalid JSON into an unfiltered list and lets `conditionToSql`
 * silently drop an unknown field), `filters` HERE is a hard 400 on both
 * failure modes — see `createGetFlowPlaysHandler` (`src/api/flow-plays.ts`).
 * Deliberate divergence (plan-review amendment on U9): the Plays list's own
 * filters are meant to be a small, fully hand-rebuildable set over
 * `PLAYS_FILTER_FIELDS`/`PLAYS_FIELD_MAP`, so a typo'd field name failing
 * loudly is more useful than a silently-wider "every play" result — unlike
 * the Contact catalog, which is large enough that a stale/old-shaped saved
 * view degrading to unfiltered (rather than 400ing a rep's whole page load)
 * is the better failure mode there.
 */
export const FlowPlaysQuerySchema = z.object({
  filters: z.string().optional(),      // JSON-encoded FilterGroup[]
  /**
   * Free-text narrowing on the play NAME (plan amendment **A17**) — the same
   * `search` param shape `/api/flow/contacts` takes
   * (`ContactsQuerySchema.search`, `src/api/flow.ts`), deliberately NOT a
   * `FilterGroup`.
   *
   * Why it is not a filter: as a chip it was working state, so every
   * committed keystroke marked the view dirty and was persisted into the
   * `plays` view-state row — a rep's throwaway search returned on reload as
   * if it had been saved. Search is a transient read.
   *
   * No `.max()`, matching `ContactsQuerySchema.search` exactly: the value is
   * bound as a parameter and LIKE-escaped, so an over-long one is slow at
   * worst, never unsafe — and 400ing a rep mid-keystroke for a long paste is
   * worse than serving it. Trimming and the empty-string no-op live in
   * `listFlowPlays` (`activeSearch`), so "no search" is decided in one place.
   */
  search: z.string().optional(),
  /**
   * The OUTER combinator between `filters` groups (plan amendment **A20**),
   * default `'AND'` — the same zod as `ContactsQuerySchema.group_logic`
   * (`src/api/flow.ts`), so an unknown value is a 400 rather than a silent
   * fallback to AND (which is exactly the kind of quiet narrowing the
   * readability rule forbids).
   *
   * NAMING: wire key is snake_case `group_logic`, matching the contacts and
   * accounts routes exactly (one filter component, one wire contract). The
   * route maps it onto the service's camelCase `groupLogic`.
   * (`src/shared/flow/view-state.ts`), which is the shared schema's own
   * field name, so matching THAT keeps the client honest end to end; the
   * older snake_case spelling on the sibling routes predates that schema.
   * Called out here because a reader diffing the two handlers would
   * otherwise read this as a typo.
   */
  group_logic: GroupLogicSchema.default('AND'),
});
export type FlowPlaysQuery = z.infer<typeof FlowPlaysQuerySchema>;

/**
 * `PLAYS_FILTER_FIELDS` — the Plays list's own filter catalog (plan A2/U9),
 * a small map DISTINCT from the Contact catalog's `CONTACT_SIGNAL_FILTER_FIELDS`.
 * Backs the Filters popover over the Plays table and the server's
 * `PLAYS_FIELD_MAP` (`src/services/flow/plays.ts`) 1:1 by `key`.
 *
 * `play_type` carries NO static `values` (unlike `signal_status`'s
 * `CAMPAIGN_STATUS_ORDER`): play-type slugs/names live in the `flow_play_types`
 * table (`resolvePlayTypeName`, an async Prisma read), and this shared module
 * cannot import the service layer. Unit U9b injects labeled options
 * client-side from the already-loaded plays rows' `play_type`/`play_type_name`
 * — the same mechanism the contact catalog's `signal_subtype` uses for the
 * live play NAMES (A4's `useSignalSubtypeOptions`).
 */
export const PLAYS_FILTER_FIELDS: FilterFieldCatalogEntry[] = withHints([
  { key: 'campaign_playhouse_visible', label: 'Playhouse Visible', group: 'Play', type: 'boolean' },
  { key: 'play_type', label: 'Play Type', group: 'Play', type: 'picklist' },
  { key: 'play_name', label: 'Play Name', group: 'Play', type: 'text', allowedOperators: ['contains', 'equals'] },
  { key: 'start_date', label: 'Start Date', group: 'Play', type: 'date', granularity: 'day' },
  { key: 'end_date', label: 'End Date', group: 'Play', type: 'date', granularity: 'day' },
  // 1:1 by key with `PLAYS_FIELD_MAP`'s `has_signals` (`src/services/flow/plays.ts`).
  { key: 'has_signals', label: 'Has Signals', group: 'Play', type: 'boolean' },
  // Plain `launcher_sfdc_campaigns` columns (U13). `status` is a free-entry
  // picklist with NO `values` and no client option injection: loaded rows
  // reflect only the filtered page, so an injected list would be incomplete.
  // `city` is free text (a picklist type would drop `contains` — see
  // filter-operators.ts). Labels match `PLAYS_COLUMNS`.
  { key: 'status', label: 'Status', group: 'Play', type: 'picklist' },
  { key: 'city', label: 'City', group: 'Play', type: 'text' },
  { key: 'target_persona', label: 'Target Persona', group: 'Play', type: 'text' },
  { key: 'capacity', label: 'Capacity', group: 'Play', type: 'number' },
  { key: 'invite_subject', label: 'Subject', group: 'Play', type: 'text' },
  { key: 'invite_body', label: 'Body', group: 'Play', type: 'text' },
], PLAYS_FIELD_HINTS);

/**
 * The Plays list's default working-state group (plan A2/U9) — seeded
 * client-side on first mount when the `plays` view-state row is empty
 * (same seeding pattern `roleDefaults` uses), exactly like
 * `CHIP_NO_RECENT_QUEUE_SEND` on the Contact component. Rendered as a
 * visible, removable chip ("Playhouse Visible is true"); clearing it shows
 * every play grouped by play type (today's `scope=all`).
 */
export const CHIP_PLAYHOUSE_VISIBLE: FilterGroup = {
  id: 'chip-playhouse-visible',
  logic: 'AND',
  conditions: [
    { id: 'chip-playhouse-visible-1', field: 'campaign_playhouse_visible', operator: 'equals', value: 'true' },
  ],
};

/**
 * Second default chip (product-owner-confirmed 3-chip default, 2026-09-13) —
 * "this play has at least one signal row", backed by the new `has_signals`
 * field (`PLAYS_FIELD_MAP`, `src/services/flow/plays.ts`). Filters out
 * campaigns nobody has ever been suggested/invited/responded on, which the
 * old 1-chip default (Playhouse Visible alone) could still surface.
 */
export const CHIP_HAS_SIGNALS: FilterGroup = {
  id: 'chip-has-signals',
  logic: 'AND',
  conditions: [
    { id: 'chip-has-signals-1', field: 'has_signals', operator: 'equals', value: 'true' },
  ],
};

/**
 * `start_date` `gte` operator's value must be a real calendar date
 * (`YYYY-MM-DD`), so the third default chip's cutoff — "today minus `days`
 * days" — has to be computed fresh at seed/reset time rather than baked into
 * a static `FilterGroup` constant like the other two chips above (a stored
 * literal date would go stale the day after it was written). UTC, matching
 * `dateCastExpr`'s own UTC anchor for `date`-typed columns
 * (`condition-to-sql.ts`) — the two must agree on what "today" means or the
 * boundary day would filter differently depending on which of the two ran.
 */
function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Third default chip — "Start Date on or after `days` days ago", so a rep's
 * default view keeps recently-started AND every future play. `gte` (not
 * `in_last_days`, which also caps the UPPER bound at today and would hide
 * every upcoming play) is the `is on or after` date operator
 * (`FILTER_OPERATORS_BY_TYPE.date`, `filter-operators.ts` — a code-only
 * operator until Phase 4 promoted it into the dropdown so the chip reads
 * correctly in the popover).
 *
 * Codex correction (2026-09-14 review): this drops more than "plays that
 * started more than a week in the past". `gte` compiles to a bare
 * `col >= value` (`condition-to-sql.ts`'s date-operator switch) with no
 * `OR col IS NULL` escape — unlike `not_equals`/`not_contains`/
 * `in_last_days`, which all carry one — so a playhouse-visible campaign with
 * signals but a NULL `start_date` is ALSO excluded by this default, not just
 * an old one. This is deliberately left as-is: Playhouse's own list view
 * has the same `start_date >= today - N` requirement and therefore the same
 * implicit NULL exclusion, so this default chip matches what a rep already
 * sees there.
 *
 * 2026-09-24, PM: upcoming only — supersedes the 7-day default of
 * 2026-09-13. The default `days` is now `0` (today), so the default chip
 * keeps every upcoming play and drops everything that already started.
 */
/**
 * Reserved, product-managed id for the rolling "Start Date on/after N days
 * ago" chip — never a rep-authored filter id. `refreshRecentStartDateChip`
 * (below) uses this to find the chip inside a STORED filter set and re-derive
 * its value, rather than trusting whatever date was materialized into it the
 * last time it was persisted.
 */
export const RECENT_START_DATE_CHIP_ID = 'chip-start-date-recent';

export function buildStartDateRecentChip(days = 0): FilterGroup {
  return {
    id: RECENT_START_DATE_CHIP_ID,
    logic: 'AND',
    conditions: [
      { id: 'chip-start-date-recent-1', field: 'start_date', operator: 'gte', value: isoDateDaysAgo(days) },
    ],
  };
}

/**
 * Re-derives the rolling start-date chip's value inside an already-STORED
 * filter set (Codex corroboration bug D, 2026-09-14 review).
 *
 * `usePlaysWorkingState.ts`'s restore path used to apply a stored
 * `workingState.filter` verbatim — including this chip's `value`, which was
 * MATERIALIZED to a real calendar date the last time the row was persisted
 * (`buildStartDateRecentChip`'s doc: `gte` needs a real date, not a relative
 * expression). A restore on a LATER day than that write re-applied the SAME
 * stale date instead of recomputing "N days ago" relative to today, so the
 * window only ever rolled forward on a first-ever load or an explicit
 * "Reset filters" click — every other restore silently widened it, letting
 * arbitrarily old plays back in. This contradicts this module's own doc claim
 * (`buildPlaysDefaultFilters`) that the chip is "computed fresh on every
 * mount/reset".
 *
 * Surgical fix: only the ONE group carrying the reserved
 * `RECENT_START_DATE_CHIP_ID` is replaced, in place, with a freshly-built
 * chip — every other stored group (including one the rep genuinely edited)
 * passes through untouched. A stored set that no longer contains this chip
 * (the rep removed it) is also untouched: removal is preserved, never
 * resurrected.
 *
 * Only a group still in the MANAGED shape (`isManagedStartDateChip`) is
 * replaced. The filter popover keeps a group's id when a rep edits it, so the
 * UI detaches an edited chip to a new id (`detachEditedStartDateChip`) before
 * it reaches storage; a stored group that still carries the managed id with a
 * non-managed shape is replaced here anyway (legacy rows), which is why the
 * detach step must run on every filter change.
 */
export function refreshRecentStartDateChip(groups: FilterGroup[]): FilterGroup[] {
  return groups.map((group) => (group.id === RECENT_START_DATE_CHIP_ID ? buildStartDateRecentChip() : group));
}

/** Id an edited (no-longer-managed) rolling start-date chip is renamed to, so the daily refresh leaves it alone. */
export const EDITED_START_DATE_CHIP_ID = 'chip-start-date-edited';

/**
 * True when `group` is the rolling start-date chip exactly as
 * `buildStartDateRecentChip()` builds it today: the managed id, one condition,
 * `start_date gte <today>`.
 */
export function isManagedStartDateChip(group: FilterGroup): boolean {
  if (group.id !== RECENT_START_DATE_CHIP_ID) return false;
  if (group.conditions.length !== 1) return false;
  const [condition] = group.conditions;
  const managed = buildStartDateRecentChip().conditions[0];
  return condition.field === 'start_date' && condition.operator === 'gte' && condition.value === managed.value;
}

/**
 * A group carrying the managed id but NOT the managed shape was edited by the
 * rep (the popover keeps the id). Rename it so `refreshRecentStartDateChip`
 * stops clobbering it. Returns the SAME array when nothing changes.
 */
export function detachEditedStartDateChip(groups: FilterGroup[]): FilterGroup[] {
  if (!groups.some((group) => group.id === RECENT_START_DATE_CHIP_ID && !isManagedStartDateChip(group))) return groups;
  return groups.map((group) =>
    group.id === RECENT_START_DATE_CHIP_ID && !isManagedStartDateChip(group)
      ? { ...group, id: EDITED_START_DATE_CHIP_ID }
      : group,
  );
}

/**
 * The Plays list's full default filter set: Playhouse Visible + Has Signals
 * + Start Date on/after today. A FUNCTION, not a constant, because the third
 * chip's value is relative to "today" — computed fresh on every mount/reset
 * (`usePlaysWorkingState.ts`'s seed and its "Reset to default" button both
 * call this rather than importing a stale snapshot).
 *
 * 2026-09-24, PM: upcoming only — supersedes the 7-day default of
 * 2026-09-13.
 */
export function buildPlaysDefaultFilters(): FilterGroup[] {
  return [CHIP_PLAYHOUSE_VISIBLE, CHIP_HAS_SIGNALS, buildStartDateRecentChip()];
}

/**
 * The Plays toolbar's column catalog (amendment **A7**) — the Plays table's
 * own `Columns` popover reads this, in this order, with `name` as the pinned
 * (always-first, non-removable) key exactly as `composeColumns()` pins Name
 * on the contact/account tables.
 *
 * Deliberately NOT a `FilterFieldCatalogEntry[]`: `PLAYS_FILTER_FIELDS`
 * above is what a rep can FILTER the plays list on (server-compiled fields
 * over `PLAYS_FIELD_MAP`), which is a different set from what the table can
 * DISPLAY — everything here except the three derived counts
 * (`suggested`/`invited_sent`/`responded`, which have no field map entry) is
 * filterable. Merging the two would either invent unfilterable filter
 * fields or hide displayable columns.
 *
 * `suggested`/`invited_sent`/`responded` key off the `counts` sub-object's
 * own field names (not `counts.suggested`) so a stored column array stays a
 * flat list of keys — the column defs in `plays/playsTableConfig.tsx` are what know
 * they read out of `row.counts`.
 */
export const PLAYS_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Play' },
  { key: 'play_type_name', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'start_date', label: 'Date' },
  { key: 'city', label: 'City' },
  { key: 'target_persona', label: 'Target Persona' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'suggested', label: 'Suggested' },
  // 2026-09-22 plan — label only (see PLAY_SCOPE_LABELS' doc comment); the
  // key stays `invited_sent`, unchanged.
  { key: 'invited_sent', label: 'Not Responded' },
  { key: 'responded', label: 'Responded' },
  { key: 'invite_subject', label: 'Subject' },
  { key: 'invite_body', label: 'Body' },
];

/** The pinned, always-first, non-removable Plays column — same role `PINNED_COLUMN_KEY` plays in `FlowTable`. */
export const PLAYS_PINNED_COLUMN_KEY = 'name';

/**
 * Default visible Plays columns: every catalog entry, Subject and Body
 * included (amendment A8 originally hid them; reversed so reps see the invite
 * copy at a glance). The cells stay single-line truncated with the full value
 * on hover, so rows do not grow; full text is in the row's View Details.
 */
export const PLAYS_DEFAULT_COLUMNS: string[] = PLAYS_COLUMNS.map((c) => c.key);

/**
 * The three things a rep can group the Plays table by (amendment A7).
 * `play_type` is the default and the only one with a fixed display order
 * (`PLAY_TYPE_GROUPS`); `status`/`city` group by the row's raw value.
 */
export const PLAYS_GROUP_KEYS = ['play_type', 'status', 'city'] as const;
export type PlaysGroupKey = (typeof PLAYS_GROUP_KEYS)[number];

/** Rep-facing label for each group key — the `Group` picker's options. `play_type`'s label is "Type", matching its column. */
export const PLAYS_GROUP_LABELS: Record<PlaysGroupKey, string> = {
  play_type: 'Type',
  status: 'Status',
  city: 'City',
};

/**
 * Stored sentinel for "the rep explicitly cleared grouping". Needed because
 * `FlowWorkingState.groupBy` is `z.string().optional()`, so ABSENT already
 * means "never set" — which for the Plays table must restore the
 * `play_type` DEFAULT, not ungrouped. Without a sentinel, clearing the
 * grouping would be indistinguishable from a fresh row and silently come
 * back on reload.
 */
export const PLAYS_GROUP_BY_NONE = 'none';
export const PLAYS_GROUP_BY_DEFAULT: PlaysGroupKey = 'play_type';
export type PlaysGroupBy = PlaysGroupKey | typeof PLAYS_GROUP_BY_NONE;

/**
 * Decodes a stored `workingState.groupBy` into the Plays table's group key.
 * Absent → the `play_type` default; `'none'` → ungrouped; an unknown value
 * (a stale tab persisting a retired key) → the default rather than a
 * phantom group nothing can render.
 */
export function parsePlaysGroupBy(stored: string | undefined): PlaysGroupBy {
  if (stored === PLAYS_GROUP_BY_NONE) return PLAYS_GROUP_BY_NONE;
  return (PLAYS_GROUP_KEYS as readonly string[]).includes(stored ?? '')
    ? (stored as PlaysGroupKey)
    : PLAYS_GROUP_BY_DEFAULT;
}

/**
 * A17 — the Plays toolbar's Search box is NO LONGER a filter chip.
 *
 * Round 2 built it as a `play_name contains` `FilterGroup` with the stable id
 * `chip-play-name-search`, which meant every committed keystroke was a
 * working-state mutation: it marked the view dirty ("Modified" / "Reset" /
 * "Save as new view"), and `useFlowViewStatePersist` PUT it into the `plays`
 * view-state row — so a rep's throwaway search came back on reload as if they
 * had saved it as a filter.
 *
 * A17 replaces it with a `search` REQUEST PARAM
 * (`FlowPlaysQuerySchema.search` above, compiled by `listFlowPlays`), held in
 * plain React state by `PlaysSubtab` and never persisted. `CHIP_PLAY_NAME_SEARCH_ID`,
 * `buildPlayNameSearchGroup`, `playNameSearchValueOf` and `withPlayNameSearch`
 * are deleted along with `plays/usePlayNameSearch.ts`.
 *
 * The `play_name` entry in `PLAYS_FILTER_FIELDS` STAYS: an explicit,
 * savable "Play Name contains …" filter a rep builds in the popover is a
 * different thing from the transient search box, and only the latter was the
 * problem.
 */

/** The `PLAYS_FILTER_FIELDS` key whose `values` are injected from live rows rather than declared statically. */
const PLAY_TYPE_FIELD_KEY = 'play_type';

/**
 * The loaded plays as `{ value: play_type, label: play_type_name }` option
 * pairs, deduped by slug in first-seen order (A2 Q2's resolution: the play
 * type list is live data in `flow_play_types`, which this shared catalog
 * module cannot read, so the options are injected client-side from rows the
 * surface has already fetched — the same mechanism the contact catalog's
 * "Play" field uses).
 */
export function playTypeOptionsOf(plays: FlowPlaysRow[]): Array<{ value: string; label: string }> {
  const seen = new Map<string, string>();
  for (const play of plays) {
    if (!seen.has(play.play_type)) seen.set(play.play_type, play.play_type_name);
  }
  return [...seen].map(([value, label]) => ({ value, label }));
}

/**
 * `PLAYS_FILTER_FIELDS` with `play_type`'s live options injected. Returns the
 * SAME array reference when there is nothing to inject, and passes every
 * other entry through by reference — a loading/error state must not churn the
 * identity of the array feeding `FilterPopover`/`useFilterDraft` (identical
 * contract to the contact catalog's play-options injection).
 */
export function withPlayTypeOptions(
  fields: FilterFieldCatalogEntry[],
  options: Array<{ value: string; label: string }>,
): FilterFieldCatalogEntry[] {
  if (options.length === 0) return fields;
  if (!fields.some((f) => f.key === PLAY_TYPE_FIELD_KEY)) return fields;
  return fields.map((f) => (f.key === PLAY_TYPE_FIELD_KEY ? { ...f, values: options } : f));
}

/**
 * ONE normalization of a play NAME, shared by every code path that has to
 * decide whether two plays are "the same play" (Codex round-1 #3): the
 * count fold in `listFlowPlays`, its per-row count lookup, the Plays table's
 * scoped-row highlight, `PlayRowActionsMenu`'s checked state, and A10's
 * scoped count.
 *
 * `toLowerCase()` and NOTHING ELSE, because that is EXACTLY the predicate
 * those counts back: `LOWER(col) = LOWER(val)` in `buildPredicate`
 * (`condition-to-sql.ts`). The key must fold precisely what SQL folds —
 * over-folding is as wrong as under-folding, and both have now shipped as
 * bugs:
 *
 *  - UNDER-folding (Codex round-1 #3, fixed): a case-SENSITIVE key against a
 *    case-INSENSITIVE predicate made plays named `Launch` and `launch` each
 *    read `1` while clicking either preset returned both contacts.
 *  - OVER-folding (Codex round-2 #5, fixed here): `trim()` collapsed `Launch`
 *    and ` Launch ` into one key, which SQL treats as two distinct values. Only
 *    the first spelling reached the `in_any_of` list, so both rows displayed
 *    that spelling's count, and clicking the whitespace-bearing row scoped on
 *    the untrimmed value and returned a different number. Trimming would only
 *    be correct if the SQL side also `BTRIM`'d, which it does not.
 *  - `toLocaleLowerCase()` (also #5) folded by the JS runtime's DEFAULT
 *    locale, which Postgres's `LOWER()` does not share: in a Turkish locale
 *    `I` lowercases to `ı` in JS and to `i` in Postgres, so key and predicate
 *    would disagree per-environment. `toLowerCase()` is locale-independent.
 *
 * The original spelling is always retained for DISPLAY and as the filter
 * VALUE; this key is never persisted or shown.
 */
export function playNameKey(name: string): string {
  return name.toLowerCase();
}

/**
 * Contact filter-field keys that USED to exist and no longer do (Codex
 * round-1 #1). A condition on one of these compiles to nothing — the field
 * has no `CONTACT_FIELD_MAP` entry, so `conditionToSql` drops its predicate
 * — while its SIBLING conditions in the same AND group still run. A
 * persisted A1 preset (`signal_instance_id equals 701… AND signal_status
 * equals Suggested`) therefore silently WIDENS to "Suggested contacts from
 * every play" while the UI still claims to be scoped to one play. Dropping
 * the whole group is the only honest restore.
 *
 * `signal_instance_id` is the only entry: A4 deleted it from the contact
 * catalog when presets moved to `signal_subtype equals "<play name>"`.
 * Add a key here whenever a catalog field is retired, and the sanitizer
 * below handles the stale rows without further code.
 */
export const RETIRED_CONTACT_FILTER_FIELDS = ['signal_instance_id', 'action_completed_by_name'] as const;

/**
 * Sanitizes a restored set of working `FilterGroup`s (Codex round-1 #1) by
 * dropping, WHOLE, any group (under ANY id) holding a condition on a
 * `RETIRED_CONTACT_FILTER_FIELDS` key. That predicate silently vanishes
 * server-side — `CONTACT_FIELD_MAP` has no entry for it — leaving the
 * group's SIBLING conditions running wider than the rep can see: a live,
 * invisible narrower/wider mismatch on the wire. That group cannot honestly
 * run, so it goes.
 *
 * ── That is the WHOLE rule (A27) ─────────────────────────────────────────
 * Round 3 also dropped any `PLAY_SCOPE_GROUP_ID` group `matchPlayScope`
 * could not decode. That cost a rep their entire filter group the moment
 * they extended a preset ("Responded" + `title contains VP`) or flipped its
 * combinator: the group still RAN, every condition in it was visible and
 * rep-readable, and reload deleted it AND persisted the deletion (Codex
 * round-3 **#5**). Unrecognised-but-runnable is not a defect to repair —
 * the only honest consequence is that `matchPlayScope` returns `null`, so
 * nothing highlights a row, checks a menu item, or runs A10's scoped-count
 * decision. The dirty-state / save-view model (Modified / Reset / Save as
 * new view) is what tells the rep their working filters diverge from the
 * view; it needs no help from a sanitizer.
 *
 * Returns the SAME reference when nothing changed, so the caller can make
 * the persist write conditional on `next !== prev` and not churn an
 * already-clean row (U16 relies on this: only a real change may PUT, or the
 * sanitizer would fight `useFlowViewStatePersist`'s baseline).
 *
 * Deliberately NOT a repair: there is no correct narrower filter to
 * substitute for a retired id predicate, and guessing one would reintroduce
 * the hidden filter this exists to remove. Wider-but-visible beats
 * narrower-but-invisible.
 */
export function sanitizePlayScopeFilters(groups: FilterGroup[]): FilterGroup[] {
  const retired = new Set<string>(RETIRED_CONTACT_FILTER_FIELDS);
  const kept = groups.filter((g) => !g.conditions.some((c) => retired.has(c.field)));
  return kept.length === groups.length ? groups : kept;
}
